// src/pages/inventory/RapTransactions.js
import React from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, TextField, MenuItem, Button,
  Tabs, Tab, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Divider, Chip, Stack
} from '@mui/material';
import { Autocomplete } from '@mui/material';

import Client from './components/Client';
import Config from './components/ClientConfig';

// --- CSV helpers ---
function toCsvValue(v) {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
function buildCsv(columns, rows) {
  const header = columns.map(c => toCsvValue(c.label)).join(',');
  const lines = rows.map(r => columns.map(c => toCsvValue(r[c.key])).join(','));
  return [header, ...lines].join('\n');
}
function downloadCsv(text, fname) {
  const blob = new Blob(["\uFEFF" + text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const compactFieldSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 140,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactDateSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 130,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    height: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const compactFilterPaperSx = {
  flexShrink: 0,
  p: {
    xs: 0.5,
    sm: 0.75,
  },
  mb: 0.75,
  borderRadius: '14px',
  overflow: 'hidden',
};

const compactTablePaperSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};

const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  width: '100%',
};

const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

const compactTableSx = {
  minWidth: {
    xs: 760,
    sm: 900,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
};

class RapTransactions extends React.Component {
  constructor(props) {
    super(props);

    const today = dayjs().format('YYYY-MM-DD');
    const firstOfMonth = dayjs().startOf('month').format('YYYY-MM-DD');

    this.state = {
      client: new Client('none'),

      activeTab: 0,
      loading: false,

      // Lookups
      sourceTypes: [],          // [[id, label], ...]
      docTypes: [],             // [{ iref, type, ... }]
      locations: [],            // [{ iref, name, ... }]
      products: [],             // [{ iref/code, name, ... }]
      sourceTypeMap: {},        // { id: label }
      docTypeMap: {},           // { iref: type }
      productNameMap: {},       // { productIdOrCode: name }

      // Filters per tab (location required)
      txFilters:   { dateFrom: firstOfMonth, dateTo: today, location: '', docType: '', sourceType: '' },
      dateFilters: { dateFrom: firstOfMonth, dateTo: today, location: '', product: '', docType: '' },
      locFilters:  { dateFrom: firstOfMonth, dateTo: today, location: '', product: '', docType: '' },
      prodFilters: { dateFrom: firstOfMonth, dateTo: today, location: '', product: '', docType: '' },

      // 🚀 Decisions tab filters
      decisionsFilters: { period: dayjs().format('YYYY-MM'), location: '', product: '' },

      // Data per tab
      txRows: [],
      summaryDate: [],
      summaryLocation: [],
      summaryProduct: [],

      // Keep originals for local Product filtering
      all_summaryDate: null,
      all_summaryLocation: null,
      all_summaryProduct: null,

      // 🚀 Decisions data
      decisionsAll: [],
      decisionsRows: [],

      // UI state
      page: 0,
      rowsPerPage: 20,
      openSet: new Set(),

      // 🚀 Decisions pagination
      decisionsPage: 0,
      decisionsRpp: 50,

      // Lazy flags
      loadedTx: false,
      loadedDate: false,
      loadedLoc: false,
      loadedProd: false,

      // 🚀 Decisions lazy flag/loading
      decisionsLoaded: false,
      decisionsLoading: false,
    };
  }

  componentDidMount() {
    this.fetchLookups();
  }

  // ---------- utils ----------
  numberFormat = (val, digits = 2) =>
    val == null || val === '' ? '' : Number(val).toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  clampTo60Days = (fromISO, toISO) => {
    const from = dayjs(fromISO);
    const to = dayjs(toISO);
    if (to.diff(from, 'day') > 60) {
      const cappedTo = from.add(60, 'day');
      const today = dayjs();
      return { dateFrom: fromISO, dateTo: (cappedTo.isAfter(today) ? today : cappedTo).format('YYYY-MM-DD') };
    }
    return { dateFrom: fromISO, dateTo: toISO };
  };

  getSelectedProductName(prodId) {
    if (!prodId) return '';
    const p = (this.state.products || []).find(pp =>
      String(pp.iref || pp.id || pp.uid || pp.code) === String(prodId)
    );
    return p ? (p.name || p.productName || p.description || p.code || String(prodId)) : String(prodId);
  };

  getLocationName(locId) {
    if (!locId) return '';
    const l = (this.state.locations || []).find(ll =>
      String(ll.iref || ll.id || ll.uid) === String(locId)
    );
    return l ? (l.name || l.description || String(locId)) : String(locId);
  };

  getDocTypeName(docId) {
    return docId ? (this.state.docTypeMap[docId] || String(docId)) : '';
  };

  normalizeTxt(s) {
    return (s ?? '').toString().trim().toLowerCase();
  };

  applyDateRangeLocal(list, fromISO, toISO) {
    if (!Array.isArray(list) || !list.length) return [];
    const from = dayjs(fromISO, 'YYYY-MM-DD');
    const to   = dayjs(toISO,   'YYYY-MM-DD');

    if (!('transDate' in (list[0] || {}))) return list;

    return list.filter(r => {
      const d = dayjs(r.transDate);
      if (!d.isValid()) return false;
      const gteFrom = d.isAfter(from, 'day') || d.isSame(from, 'day');
      const lteTo   = d.isBefore(to,   'day') || d.isSame(to,   'day');
      return gteFrom && lteTo;
    });
  };

  formatQty = (val) => {
    if (val == null || val === '') return '';
    const num = Number(val);
    if (!Number.isFinite(num)) return val;

    // round to 3 decimals
    const rounded = Math.round(num * 1000) / 1000;
    // strip trailing zeros
    return rounded.toString();
  };
  
  // ---------- build query payloads ----------
  buildTxQuery = () => {
    const { dateFrom, dateTo, location, docType, sourceType } = this.state.txFilters;
    const c = this.clampTo60Days(dateFrom, dateTo);
    const q = { dateFrom: c.dateFrom, dateTo: c.dateTo, date_from: c.dateFrom, date_to: c.dateTo };
    if (location) q.location = location; // required in UI
    if (docType)  q.docType  = docType;
    if (sourceType) q.dataSource = sourceType;
    return q;
  };

  buildDateQuery = () => {
    const { dateFrom, dateTo, location, /*product,*/ docType } = this.state.dateFilters;
    const c = this.clampTo60Days(dateFrom, dateTo);
    const q = { dateFrom: c.dateFrom, dateTo: c.dateTo, date_from: c.dateFrom, date_to: c.dateTo };
    if (location) q.location = location; // required in UI
    if (docType)  q.docType  = docType;
    return q;
  };

  buildLocQuery = () => {
    const { dateFrom, dateTo, location, /*product,*/ docType } = this.state.locFilters;
    const c = this.clampTo60Days(dateFrom, dateTo);
    const q = { dateFrom: c.dateFrom, dateTo: c.dateTo, date_from: c.dateFrom, date_to: c.dateTo };
    if (location) q.location = location; // required in UI
    if (docType)  q.docType  = docType;
    return q;
  };

  buildProdQuery = () => {
    const { dateFrom, dateTo, location, /*product,*/ docType } = this.state.prodFilters;
    const c = this.clampTo60Days(dateFrom, dateTo);
    const q = { dateFrom: c.dateFrom, dateTo: c.dateTo, date_from: c.dateFrom, date_to: c.dateTo };
    if (location) q.location = location; // required in UI
    if (docType)  q.docType  = docType;
    return q;
  };

  // 🚀 decisions query (year/month + location; product filtered locally)
  buildDecisionsQuery = () => {
    const { period, location } = this.state.decisionsFilters;
    const [year, month] = (period || '').split('-');
    const q = { year, month };
    if (location) q.location = location; // required in UI
    return q;
  };

  // ---------- lookups ----------
  fetchLookups() {
    const { client } = this.state;
    this.setState({ loading: true });

    const sourceTypes = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}source_types/`, {}) || [];
    const docTypes    = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}doc_types/`, {}) || [];

    const sourceTypeMap = {};
    (sourceTypes || []).forEach(([id, label]) => { sourceTypeMap[id] = label; });

    const docTypeMap = {};
    (docTypes || []).forEach((dt) => { docTypeMap[dt.iref] = dt.type; });

    const locationsResp = client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const productsResp  = client.getData(Config.API_URL_PRODUCT + 'list_products/', { status: 2, orderBy_name: true }) || {};

    const locations = Array.isArray(locationsResp.locations) ? locationsResp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const products  = Array.isArray(productsResp.products) ? productsResp.products : [];

    const productNameMap = {};
    products.forEach((p) => {
      const pid = p.iref || p.uid || p.code || p.product || p.id;
      const pname = p.name || p.productName || p.description || p.code || pid;
      if (pid) productNameMap[String(pid)] = String(pname);
    });

    // NOTE: do NOT auto-fetch data here; require the user to pick a location first.
    this.setState({
      sourceTypes, docTypes, locations, products,
      sourceTypeMap, docTypeMap, productNameMap,
      loading: false,
    }, () => {
      if (this.props.onReady) {
        this.props.onReady();
      }
    });
  }

  // ---------- fetch per tab ----------
  fetchTx() {
    const { client, docTypeMap, sourceTypeMap, productNameMap, txFilters } = this.state;

    if (!txFilters.location) return; // guard: location required

    const query = this.buildTxQuery();
    this.setState({ loading: true, page: 0 });

    const txData = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}`, query) || {};
    const txs = Array.isArray(txData.inventoryTransactions) ? txData.inventoryTransactions : [];

    const from = dayjs(txFilters.dateFrom, 'YYYY-MM-DD');
    const to   = dayjs(txFilters.dateTo,   'YYYY-MM-DD');
    const inRange = (iso) => {
      const d = dayjs(iso);
      if (!d.isValid()) return false;
      return (d.isAfter(from, 'day') || d.isSame(from, 'day')) &&
            (d.isBefore(to,  'day') || d.isSame(to,  'day'));
    };
    const txsFiltered = txs.filter(tx => inRange(tx.date));

    const nameCache = { ...(productNameMap || {}) };

    const txRows = txsFiltered.map((tx) => {
      const mappedLines = Array.isArray(tx.inventoryTransactionsLines_invTransaction)
        ? tx.inventoryTransactionsLines_invTransaction.map((ln) => {
            const qty = Number(ln.quantity || 0);
            const price = Number(ln.price || 0);
            const rawId = ln.product;

            const resolvedName =
              ln.productName ||
              ln.product__name ||
              productNameMap[String(rawId)] ||
              String(rawId || '');

            const finishedResolved =
              productNameMap[String(ln.finishedProduct)] || '';

            return {
              id: ln.iref,
              product: resolvedName,
              finishedProduct: finishedResolved,
              qty,
              value: qty * price,
            };
          })
        : [];

      const extDocNo = tx.extDocNo || tx.ext_docNo || '';

      return {
        id: tx.iref,
        rawDate: tx.date,
        date: dayjs(tx.date).format('DD MMM YYYY'),
        location: tx.location?.name || '',
        docType: docTypeMap[tx.docType?.iref] || tx.docType?.type || '',
        dataSource: sourceTypeMap[tx.dataSource] || tx.dataSource,
        docNo: extDocNo,
        lines: mappedLines,
        linesCount: mappedLines.length,
      };
    });

    this.setState({
      txRows,
      productNameMap: nameCache,
      loading: false,
      loadedTx: true,
      openSet: new Set(),
    });
  }

  fetchDateSummary() {
    const { client, dateFilters, productNameMap } = this.state;
    if (!dateFilters.location) return; // guard: location required

    const query = this.buildDateQuery();
    this.setState({ loading: true, page: 0 });

    let list = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}summary_by_date/`, query) || [];
    if (!Array.isArray(list)) list = [];

    const enriched = list.map(r => {
      const finishedDisplay =
        r.transFinishedName ||
        productNameMap[String(r.transFinishedIref)] ||
        (r.transFinishedIref ? String(r.transFinishedIref) : '');
      return { ...r, finishedDisplay };
    });

    this.setState({
      all_summaryDate: list,
      summaryDate: this.applyLocalProductFilter(enriched, dateFilters.product),
      loading: false,
      loadedDate: true,
    });
  }

  fetchLocSummary() {
    const { client, locFilters, productNameMap } = this.state;
    if (!locFilters.location) return; // guard: location required

    const query = this.buildLocQuery();
    this.setState({ loading: true, page: 0 });

    let list = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}summary_by_location/`, query) || [];
    if (!Array.isArray(list)) list = [];

    const enriched = list.map(r => {
      const finishedDisplay =
        r.transFinishedName ||
        productNameMap[String(r.transFinishedIref)] ||
        (r.transFinishedIref ? String(r.transFinishedIref) : '');
      return { ...r, finishedDisplay };
    });

    this.setState({
      all_summaryLocation: list,
      summaryLocation: this.applyLocalProductFilter(enriched, locFilters.product),
      loading: false,
      loadedLoc: true,
    });
  }

  fetchProdSummary() {
    const { client, prodFilters, productNameMap } = this.state;
    if (!prodFilters.location) return; // guard: location required

    const query = this.buildProdQuery();
    this.setState({ loading: true, page: 0 });

    let list = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}summary_by_product/`, query) || [];
    if (!Array.isArray(list)) list = [];

    const enriched = list.map(r => {
      const finishedDisplay =
        r.transFinishedName ||
        productNameMap[String(r.transFinishedIref)] ||
        (r.transFinishedIref ? String(r.transFinishedIref) : '');
      return { ...r, finishedDisplay };
    });

    this.setState({
      all_summaryProduct: list,
      summaryProduct: this.applyLocalProductFilter(enriched, prodFilters.product),
      loading: false,
      loadedProd: true,
    });
  }

  // 🚀 decisions fetch
  fetchDecisions = () => {
    const { client, decisionsFilters } = this.state;
    if (!decisionsFilters.location) return; // guard: location required

    this.setState({ decisionsLoading: true, decisionsPage: 0 });

    const query = this.buildDecisionsQuery(); // { year, month, location }
    const resp = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}month_decisions/`, query) || {};
    const list = Array.isArray(resp.results) ? resp.results : [];

    const prod = decisionsFilters.product || '';
    const filtered = prod
      ? list.filter(r => String(r.productIref) === String(prod))
      : list;

    this.setState({
      decisionsAll: list,
      decisionsRows: filtered,
      decisionsLoading: false,
      decisionsLoaded: true,
    });
  };

  // local product filter helper for summaries
  applyLocalProductFilter(list, productId) {
    if (!productId) return list;
    const target = this.normalizeTxt(this.getSelectedProductName(productId));
    return (list || []).filter(r => this.normalizeTxt(r.transProduct) === target);
  }

  // ---------- events ----------
  onTabChange = (_e, newTab) => {
    this.setState({ activeTab: newTab, page: 0 }, () => {
      // Do NOT auto-fetch without a location selected
      if (newTab === 1 && this.state.dateFilters.location && !this.state.loadedDate) this.fetchDateSummary();
      if (newTab === 2 && this.state.locFilters.location  && !this.state.loadedLoc)  this.fetchLocSummary();
      if (newTab === 3 && this.state.prodFilters.location && !this.state.loadedProd) this.fetchProdSummary();
      if (newTab === 4 && this.state.decisionsFilters.location && !this.state.decisionsLoaded) this.fetchDecisions();
    });
  };

  // Per-tab Apply
  onApplyTx   = () => this.fetchTx();
  onApplyDate = () => this.fetchDateSummary();
  onApplyLoc  = () => this.fetchLocSummary();
  onApplyProd = () => this.fetchProdSummary();

  handleDateChange = (scope, name, value) => {
    this.setState((prev) => {
      const next = { ...prev };
      next[scope] = { ...prev[scope], [name]: value };
      const { dateFrom, dateTo } = next[scope];
      const c = this.clampTo60Days(dateFrom, dateTo);
      next[scope].dateFrom = c.dateFrom;
      next[scope].dateTo = c.dateTo;
      return next;
    });
  };

  setFilter = (scope, name, value) => {
    this.setState((prev) => ({ ...prev, [scope]: { ...prev[scope], [name]: value } }));
  };

  resetFilters(scopeKey) {
    const today = dayjs();
    const startOfMonth = today.startOf('month').format('YYYY-MM-DD');
    const todayStr = today.format('YYYY-MM-DD');

    const defaults = {
      dateFrom: startOfMonth,
      dateTo: todayStr,
      location: '',
      product: '',
      docType: '',
      sourceType: '',
    };

    this.setState({ [scopeKey]: { ...defaults } }, () => {
      if (scopeKey === 'dateFilters' && this.state.all_summaryDate) {
        this.setState({ summaryDate: [...this.state.all_summaryDate] });
      }
      if (scopeKey === 'locFilters' && this.state.all_summaryLocation) {
        this.setState({ summaryLocation: [...this.state.all_summaryLocation] });
      }
      if (scopeKey === 'prodFilters' && this.state.all_summaryProduct) {
        this.setState({ summaryProduct: [...this.state.all_summaryProduct] });
      }
    });
  }

  onChangePage = (_e, newPage) => this.setState({ page: newPage });
  onChangeRowsPerPage = (e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 });

  toggleOpen = (id) => {
    this.setState(prev => {
      const next = new Set(prev.openSet || new Set());
      if (next.has(id)) next.delete(id); else next.add(id);
      return { openSet: next };
    });
  };

  // --- CSV export ---
  handleExportCsv = () => {
    const { activeTab, txRows, summaryDate, summaryLocation, summaryProduct, decisionsRows, decisionsAll } = this.state;
    let rows = [], columns = [];

    if (activeTab === 0) {
      // Export only detailed lines
      rows = (txRows || []).flatMap(tx => (tx?.lines || []).map(ln => ({
        date: tx.date,
        location: tx.location,
        docType: tx.docType,
        source: tx.dataSource,
        docNo: tx.docNo,
        product: ln.product,
        qty: ln.qty,
        value: ln.value,
        finishedProduct: ln.finishedProduct,
      })));
      columns = [
        { key: 'date', label: 'Date' },
        { key: 'location', label: 'Location' },
        { key: 'docType', label: 'Doc Type' },
        { key: 'source', label: 'Source' },
        { key: 'docNo', label: 'Doc No' },
        { key: 'product', label: 'Product' },
        { key: 'qty', label: 'Qty' },
        { key: 'value', label: 'Value' },
        { key: 'finishedProduct', label: 'Finished Product' },
      ];
    } else if (activeTab === 1) {
      rows = summaryDate || [];
      columns = [
        { key: 'transDate', label: 'Date' },
        { key: 'transLocation', label: 'Location' },
        { key: 'transDocType', label: 'Doc Type' },
        { key: 'transProduct', label: 'Product' },
        { key: 'qty', label: 'Qty' },
        { key: 'value', label: 'Value' },
        { key: 'finishedDisplay', label: 'Finished Product' },
      ];
    } else if (activeTab === 2) {
      rows = summaryLocation || [];
      columns = [
        { key: 'transLocation', label: 'Location' },
        { key: 'transProduct', label: 'Product' },
        { key: 'transDocType', label: 'Doc Type' },
        { key: 'qty', label: 'Qty' },
        { key: 'value', label: 'Value' },
        { key: 'finishedDisplay', label: 'Finished Product' },
      ];
    } else if (activeTab === 3) {
      rows = summaryProduct || [];
      columns = [
        { key: 'transProduct', label: 'Product' },
        { key: 'transLocation', label: 'Location' },
        { key: 'transDocType', label: 'Doc Type' },
        { key: 'qty', label: 'Qty' },
        { key: 'value', label: 'Value' },
        { key: 'finishedDisplay', label: 'Finished Product' },
      ];
    } else if (activeTab === 4) {
      // Decisions: try rows, then all; flatten nested lines if present and infer columns
      const base = (Array.isArray(decisionsRows) && decisionsRows.length)
        ? decisionsRows
        : (Array.isArray(decisionsAll) ? decisionsAll : []);

      const flattened = (base || []).flatMap(r => {
        const hasLines = r && Array.isArray(r.lines) && r.lines.length;
        if (hasLines) {
          const { lines, ...parent } = r;
          return lines.map(ln => ({ ...parent, ...ln }));
        }
        return [r];
      });

      rows = flattened;

      if (rows.length) {
        const prettyLabel = (k) => k
          .replace(/_/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, s => s.toUpperCase());

        const preferred = ['period','transPeriod','date','location','transLocation','product','transProduct','docType','transDocType','qty','value','decision','invDecisionQty','inventoryCheckQty','calcQtyBeforeAdj','diff','diffValue','cogaQty','pctDecisionVsCoga'];
        const keys = Object.keys(rows[0]);
        const ordered = [...new Set([...preferred.filter(k => keys.includes(k)), ...keys])];
        columns = ordered.map(k => ({ key: k, label: prettyLabel(k) }));
      }
    }

    if (!rows || !rows.length || !columns || !columns.length) return;
    const csv = buildCsv(columns, rows);
    const fname = `rap-transactions-tab${activeTab}-${new Date().toISOString().slice(0,10)}.csv`;
    downloadCsv(csv, fname);
  };

  // ---------- chip bar ----------
  renderActiveChips(scopeKey) {
    const f = this.state[scopeKey];
    const chips = [];

    if (f.dateFrom || f.dateTo) {
      chips.push({
        label: `Date: ${f.dateFrom || '…'} → ${f.dateTo || '…'}`,
        clear: () => this.setFilter(scopeKey, 'dateFrom', '') || this.setFilter(scopeKey, 'dateTo', '')
      });
    }
    if (f.location) {
      chips.push({
        label: `Location: ${this.getLocationName(f.location)}`,
        clear: () => this.setFilter(scopeKey, 'location', '')
      });
    }
    if (f.docType) {
      chips.push({
        label: `Doc: ${this.getDocTypeName(f.docType)}`,
        clear: () => this.setFilter(scopeKey, 'docType', '')
      });
    }
    if (scopeKey !== 'txFilters' && f.product) {
      chips.push({
        label: `Product: ${this.getSelectedProductName(f.product)}`,
        clear: () => {
          this.setFilter(scopeKey, 'product', '');
          if (scopeKey === 'dateFilters' && this.state.all_summaryDate) {
            this.setState({ summaryDate: [...this.state.all_summaryDate] });
          }
          if (scopeKey === 'locFilters' && this.state.all_summaryLocation) {
            this.setState({ summaryLocation: [...this.state.all_summaryLocation] });
          }
          if (scopeKey === 'prodFilters' && this.state.all_summaryProduct) {
            this.setState({ summaryProduct: [...this.state.all_summaryProduct] });
          }
        }
      });
    }

    if (!chips.length) return null;

    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          mt: 0.75,
          mb: 0,
          flexWrap: 'wrap',
          '& .MuiChip-root': {
            height: 24,
            maxWidth: '100%',
          },
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {chips.map((c, idx) => (
          <Chip key={idx} size="small" label={c.label} onDelete={c.clear} />
        ))}
      </Stack>
    );
  }

  // ---------- filters UI ----------
  renderTxFilters() {
    const { txFilters, docTypes, sourceTypes, locations } = this.state;
    const maxDateTo = (() => {
      const candidate = dayjs(txFilters.dateFrom).add(60, 'day');
      const today = dayjs();
      return (candidate.isAfter(today) ? today : candidate).format('YYYY-MM-DD');
    })();

   const tfSx = compactFieldSx;
    const dateSx = compactDateSx;

    const row = {
      display: 'flex',
      gap: { xs: 0.75, sm: 1 },
      flexWrap: 'wrap',
      alignItems: 'center',
      '& .MuiButton-root': compactButtonSx,
    };

    const canApply = !!txFilters.location;

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Box sx={row}>
          <TextField
            label="From" type="date" value={txFilters.dateFrom}
            onChange={(e) => this.handleDateChange('txFilters','dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: txFilters.dateTo }}
            size="small" margin="dense" sx={dateSx}
          />
          <TextField
            label="To" type="date" value={txFilters.dateTo}
            onChange={(e) => this.handleDateChange('txFilters','dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: txFilters.dateFrom, max: maxDateTo }}
            size="small" margin="dense" sx={dateSx}
          />

          <TextField
            select label="Location" value={txFilters.location}
            onChange={(e) => this.setFilter('txFilters','location', e.target.value)}
            size="small" margin="dense" sx={tfSx}
            required
            error={!txFilters.location}
            // helperText={!txFilters.location ? 'Select a location' : ' '}
          >
            {/* Removed "All" option */}
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select label="Doc Type" value={txFilters.docType}
            onChange={(e) => this.setFilter('txFilters','docType', e.target.value)}
            size="small" margin="dense" sx={tfSx}
          >
            <MenuItem value="">All</MenuItem>
            {docTypes.map((dt) => (
              <MenuItem key={dt.iref} value={dt.iref}>{dt.type}</MenuItem>
            ))}
          </TextField>

          <TextField
            select label="Source" value={txFilters.sourceType}
            onChange={(e) => this.setFilter('txFilters','sourceType', e.target.value)}
            size="small" margin="dense" sx={tfSx}
          >
            <MenuItem value="">All</MenuItem>
            {sourceTypes.map(([id, label]) => (
              <MenuItem key={id} value={id}>{label}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" size="small" onClick={() => this.resetFilters('txFilters')}>
            Reset
          </Button>
          <Button variant="contained" size="small" onClick={this.onApplyTx} disabled={!canApply}>
            Apply
          </Button>
        </Box>
        {this.renderActiveChips('txFilters')}
      </Paper>
    );
  }

  renderCommonFilters(scopeKey) {
    const filters = this.state[scopeKey];
    const { docTypes, locations, products } = this.state;

    const applyHandlers = {
      dateFilters: this.onApplyDate,
      locFilters:  this.onApplyLoc,
      prodFilters: this.onApplyProd,
    };
    const onApply = applyHandlers[scopeKey] || (() => {});

    const maxDateTo = (() => {
      const candidate = dayjs(filters.dateFrom).add(60, 'day');
      const today = dayjs();
      return (candidate.isAfter(today) ? today : candidate).format('YYYY-MM-DD');
    })();

    const tfSx = compactFieldSx;
    const dateSx = compactDateSx;

    const row = {
      display: 'flex',
      gap: { xs: 0.75, sm: 1 },
      flexWrap: 'wrap',
      alignItems: 'center',
      '& .MuiButton-root': compactButtonSx,
    };

    const canApply = !!filters.location;

    const productOptions = (products || []).map(p => ({
      value: String(p.iref || p.id || p.uid || p.code),
      label: p.name || p.productName || p.description || p.code || String(p.iref || p.id || p.uid || p.code),
    }));
    const productValue = productOptions.find(opt => opt.value === String(filters.product)) || null;

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Box sx={row}>
          <TextField
            label="From" type="date" value={filters.dateFrom}
            onChange={(e) => this.handleDateChange(scopeKey,'dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: filters.dateTo }}
            size="small" margin="dense" sx={dateSx}
          />
          <TextField
            label="To" type="date" value={filters.dateTo}
            onChange={(e) => this.handleDateChange(scopeKey,'dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: filters.dateFrom, max: maxDateTo }}
            size="small" margin="dense" sx={dateSx}
          />

          <TextField
            select label="Location" value={filters.location}
            onChange={(e) => this.setFilter(scopeKey,'location', e.target.value)}
            size="small" margin="dense" sx={tfSx}
            required
            error={!filters.location}
            // helperText={!filters.location ? 'Select a location' : ' '}
          >
            {/* Removed "All" option */}
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select label="Doc Type" value={filters.docType}
            onChange={(e) => this.setFilter(scopeKey,'docType', e.target.value)}
            size="small" margin="dense" sx={tfSx}
          >
            <MenuItem value="">All</MenuItem>
            {docTypes.map((dt) => (
              <MenuItem key={dt.iref} value={dt.iref}>{dt.type}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={() => this.resetFilters(scopeKey)}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onApply}
            disabled={!canApply}
          >
            Apply
          </Button>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: { xs: 0, sm: 0.75 },
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <Autocomplete
            size="small"
            value={productValue}
            options={productOptions}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '0 0 240px',
              },
              minWidth: 0,
            }}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';
              this.setFilter(scopeKey, 'product', newVal);

              if (scopeKey === 'dateFilters' && this.state.all_summaryDate) {
                this.setState({
                  summaryDate: this.applyLocalProductFilter(this.state.all_summaryDate, newVal),
                });
              }
              if (scopeKey === 'locFilters' && this.state.all_summaryLocation) {
                this.setState({
                  summaryLocation: this.applyLocalProductFilter(this.state.all_summaryLocation, newVal),
                });
              }
              if (scopeKey === 'prodFilters' && this.state.all_summaryProduct) {
                this.setState({
                  summaryProduct: this.applyLocalProductFilter(this.state.all_summaryProduct, newVal),
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                sx={{
                  ...compactFieldSx,
                  minWidth: {
                    xs: '100%',
                    sm: 220,
                  },
                }}
              />
            )}
          />
        </Box>
        {this.renderActiveChips(scopeKey)}
      </Paper>
    );
  }

  // ---------- tables ----------
  renderLinesTable = (lines = []) => {
    const totalVal = lines.reduce((s, l) => s + (Number(l.value) || 0), 0);

    return (
      <Box
        sx={{
          maxWidth: 600,
          p: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 320 }}>Product</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>Qty</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 260 }}>Finished Product</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((ln) => {
              const name = ln.productName || this.getSelectedProductName(ln.productId || ln.product);
              return (
                <TableRow key={ln.id}>
                  <TableCell
                    align="left"
                    title={name}
                    sx={{
                      width: 260,
                      maxWidth: 260,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {name}
                  </TableCell>
                  <TableCell align="right">
                    {Number(ln.qty || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </TableCell>
                  <TableCell align="right">
                    {Number(ln.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell
                    align="left"
                    title={ln.finishedProduct || ''}
                    sx={{
                      width: 260,
                      maxWidth: 260,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ln.finishedProduct || ''}
                  </TableCell>
                </TableRow>
              );
            })}

            <TableRow>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }} />
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {totalVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 600 }} />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  renderTxTable = () => {
    const { txRows = [], page = 0, rowsPerPage = 20, openSet = new Set(), loading } = this.state;
    const paged = txRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ width: 120, fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ minWidth: 160, fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ minWidth: 160, fontWeight: 700 }}>Doc Type</TableCell>
                <TableCell sx={{ width: 120, fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ width: 140, fontWeight: 700 }}>Doc No</TableCell>
                <TableCell align="right" sx={{ width: 90, fontWeight: 700 }}># Lines</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((row) => {
                const isOpen = openSet.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover
                      sx={{
                        bgcolor: 'grey.50',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <TableCell sx={{ py: 0.5 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => this.toggleOpen(row.id)}
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {isOpen ? '−' : '+'}
                        </Button>
                      </TableCell>

                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>{row.docType}</TableCell>
                      <TableCell>{row.dataSource}</TableCell>
                      <TableCell>{row.docNo || ''}</TableCell>
                      <TableCell align="right">{row.linesCount || 0}</TableCell>
                    </TableRow>

                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: 'background.default' }}>
                          <Box sx={{ mt: 1 }}>
                            {this.renderLinesTable(row.lines)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {!paged.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No transactions</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={txRows.length}
          page={page}
          onPageChange={(_e, newPage) => this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
          rowsPerPageOptions={[20, 50, 100]}
          sx={compactPaginationSx}
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  };

  renderSummaryTable(kind) {
    const { loading, page, rowsPerPage, summaryDate, summaryLocation, summaryProduct } = this.state;
    const nf = (v) => this.numberFormat(v, 2);

    let columns = [], data = [];
    if (kind === 'date') {
      columns = [
        { key: 'transDate',     label: 'Date' },
        { key: 'transLocation', label: 'Location' },
        { key: 'transDocType',  label: 'Doc Type' },
        { key: 'transProduct',  label: 'Product' },
        { key: 'qty',           label: 'Qty',   align: 'right', fmt: nf },
        { key: 'value',         label: 'Value', align: 'right', fmt: nf },
        { key: 'finishedDisplay',  label: 'Finished Product' },
      ];
      data = summaryDate;
    } else if (kind === 'loc') {
      columns = [
        { key: 'transLocation', label: 'Location' },
        { key: 'transProduct',  label: 'Product' },
        { key: 'transDocType',  label: 'Doc Type' },
        { key: 'qty',           label: 'Qty',   align: 'right', fmt: nf },
        { key: 'value',         label: 'Value', align: 'right', fmt: nf },
        { key: 'finishedDisplay',  label: 'Finished Product' },
      ];
      data = summaryLocation;
    } else {
      columns = [
        { key: 'transProduct',  label: 'Product' },
        { key: 'transLocation', label: 'Location' },
        { key: 'transDocType',  label: 'Doc Type' },
        { key: 'qty',           label: 'Qty',   align: 'right', fmt: nf },
        { key: 'value',         label: 'Value', align: 'right', fmt: nf },
        { key: 'finishedDisplay',  label: 'Finished Product' },
      ];
      data = summaryProduct;
    }

    const rows = (data || []).map((r, i) => ({ id: `${kind}-${i}`, ...r }));
    const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell sx={{ fontWeight: 700 }} key={c.key} align={c.align || 'left'}>{c.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((row) => (
                <TableRow key={row.id} hover>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || 'left'}>
                      {c.fmt ? c.fmt(row[c.key]) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!paged.length && !loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={this.state.page}
          onPageChange={this.onChangePage}
          rowsPerPage={this.state.rowsPerPage}
          onRowsPerPageChange={this.onChangeRowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          sx={compactPaginationSx}
        />

        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  // 🚀 Decisions UI
  renderDecisionsFilters = () => {
    const { decisionsFilters, locations, products } = this.state;
    const productOptions = (products || []).map(p => ({
      value: String(p.iref || p.id || p.uid || p.code),
      label: p.name || p.productName || p.description || p.code || String(p.iref || p.id || p.uid || p.code),
    }));
    const productValue = productOptions.find(opt => opt.value === String(decisionsFilters.product)) || null;

    const canApply = !!decisionsFilters.location;

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Box 
          sx={{
            display: 'flex',
            gap: { xs: 0.75, sm: 1 },
            flexWrap: 'wrap',
            alignItems: 'center',
            '& .MuiButton-root': compactButtonSx,
          }}
        >
          <TextField
            label="Period"
            type="month"
            value={decisionsFilters.period}
            onChange={(e) => this.setState({ decisionsFilters: { ...decisionsFilters, period: e.target.value } })}
            size="small"
            margin="dense"
            sx={compactDateSx}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Location"
            value={decisionsFilters.location}
            onChange={(e) => this.setState({ decisionsFilters: { ...decisionsFilters, location: e.target.value } })}
            size="small"
            margin="dense"
            sx={compactFieldSx}
            required
            error={!decisionsFilters.location}
            // helperText={!decisionsFilters.location ? 'Select a location' : ' '}
          >
            {/* Removed "All" option */}
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <Button variant="outlined" size="small" onClick={() => this.setState({ decisionsFilters: { period: dayjs().format('YYYY-MM'), location: '', product: '' } })}>
            Reset
          </Button>
          <Button variant="contained" size="small" onClick={this.fetchDecisions} disabled={!canApply}>
            Apply
          </Button>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: { xs: 0, sm: 0.75 },
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <Autocomplete
            size="small"
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '0 0 240px',
              },
              minWidth: 0,
            }}
            value={productValue}
            options={productOptions}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';
              this.setState((prev) => {
                const all = prev.decisionsAll || [];
                const rows = newVal ? all.filter(r => String(r.productIref) === String(newVal)) : all;
                return {
                  decisionsFilters: { ...prev.decisionsFilters, product: newVal },
                  decisionsRows: rows,
                  decisionsPage: 0,
                };
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                sx={{
                  ...compactFieldSx,
                  minWidth: {
                    xs: '100%',
                    sm: 220,
                  },
                }}
              />
            )}
          />
        </Box>
      </Paper>
    );
  };

  renderDecisionsTable = () => {
    const { decisionsRows, decisionsLoading, decisionsPage, decisionsRpp, productNameMap } = this.state;
    const paged = decisionsRows.slice(decisionsPage * decisionsRpp, decisionsPage * decisionsRpp + decisionsRpp);
    // const nf = (n, d = 4) => (n == null ? '' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Calc Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Decision Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Δ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Doc No</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((r) => {
                const productLabel = r.productName || productNameMap[String(r.productIref)] || r.productIref;
                const deltaColor = Number(r.delta) > 0 ? 'green' : Number(r.delta) < 0 ? 'red' : undefined;
                const sourceText = r.source === 0 ? 'Calculated' : r.source === 1 ? 'Inventory' : 'Manual';
                return (
                  <TableRow key={r.iref} hover>
                    <TableCell>{dayjs(r.date).format('DD MMM YYYY')}</TableCell>
                    <TableCell title={r.productIref}>{productLabel}</TableCell>
                    <TableCell align="right">{sourceText}</TableCell>
                    <TableCell align="right">{this.formatQty(r.calcQty)}</TableCell>
                    <TableCell align="right">{this.formatQty(r.decisionQty)}</TableCell>
                    <TableCell align="right" sx={{ color: deltaColor }}>{this.formatQty(r.delta)}</TableCell>
                    <TableCell>{r.docNo || ''}</TableCell>
                  </TableRow>
                );
              })}
              {!paged.length && !decisionsLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={decisionsRows.length}
          page={decisionsPage}
          onPageChange={(_e, p) => this.setState({ decisionsPage: p })}
          rowsPerPage={decisionsRpp}
          onRowsPerPageChange={(e) => this.setState({ decisionsRpp: Number(e.target.value), decisionsPage: 0 })}
          rowsPerPageOptions={[20, 50, 100]}
          sx={compactPaginationSx}
        />

        {decisionsLoading && (
          <Box sx={{
            position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  };

  render() {
    const { activeTab } = this.state;

    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,

          height: {
            xs: 'calc(100dvh - 96px)',
            sm: 'calc(100dvh - 104px)',
            md: 700,
            lg: 740,
          },
          maxHeight: {
            xs: 'calc(100dvh - 96px)',
            sm: 'calc(100dvh - 104px)',
            md: 700,
            lg: 740,
          },
          minHeight: 0,

          bgcolor: '#f3f6fa',
          overflow: 'hidden',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',

          '& .MuiPaper-root': {
            maxWidth: '100%',
            boxSizing: 'border-box',
            borderRadius: { xs: '14px', sm: '16px' },
          },

          '& .MuiTableContainer-root': {
            maxWidth: '100%',
          },

          '& table': {
            maxWidth: '100%',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            mb: 0.75,
            overflow: 'hidden',
            borderRadius: '12px',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 3px 10px rgba(15, 23, 42, 0.07)',
            bgcolor: 'rgba(248,250,252,0.98)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              px: { xs: 0.5, sm: 1 },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={this.onTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                flexGrow: 1,
                minWidth: 0,
                minHeight: { xs: 38, sm: 42 },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: 2,
                },
                '& .MuiTab-root': {
                  minHeight: { xs: 38, sm: 42 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: 11.5, sm: 13 },
                  px: { xs: 0.8, sm: 1.35 },
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <Tab label="Tx" />
              <Tab label="Date" />
              <Tab label="Loc" />
              <Tab label="Prod" />
              <Tab label="Decisions" />
            </Tabs>

            <Button
              onClick={this.handleExportCsv}
              size="small"
              variant="outlined"
              sx={{
                ...compactButtonSx,
                flexShrink: 0,
                minWidth: { xs: 56, sm: 82 },
                px: { xs: 0.75, sm: 1.25 },
                fontSize: { xs: 11.5, sm: 12.5 },
              }}
            >
              CSV
            </Button>
          </Box>
        </Paper>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',

            '& .MuiPaper-root': {
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
            },

            '& .MuiTextField-root': {
              maxWidth: '100%',
            },

            '& .MuiButton-root': {
              textTransform: 'none',
            },

            '& .MuiTableCell-root': {
              fontSize: { xs: 12.5, sm: 13 },
              px: { xs: 0.75, sm: 1 },
              py: { xs: 0.65, sm: 0.8 },
            },

            '& .MuiTablePagination-root': {
              overflowX: 'auto',
            },
          }}
        >
          {activeTab === 0 && (
            <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderTxFilters()}
              {this.renderTxTable()}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderCommonFilters('dateFilters')}
              {this.renderSummaryTable('date')}
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderCommonFilters('locFilters')}
              {this.renderSummaryTable('loc')}
            </Box>
          )}

          {activeTab === 3 && (
            <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderCommonFilters('prodFilters')}
              {this.renderSummaryTable('prod')}
            </Box>
          )}

          {activeTab === 4 && (
            <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderDecisionsFilters()}
              {this.renderDecisionsTable()}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
}

export default RapTransactions;